import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Logo } from "./Logo";
import { useAuth } from "../context/AuthContext";
import { LayoutDashboard, Users, UserCircle, LogOut, Menu, X, Moon, Sun, ShieldCheck, CalendarDays, Gem, Users2, BarChart3, ScrollText, Landmark } from "lucide-react";
import { ToothIcon } from "../pages/Odontogram";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import FloatingCalculator from "./FloatingCalculator";
import FloatingClinIV from "./FloatingClinIV";
import FloatingClinPulse from "./FloatingClinPulse";
import FloatingClinBreath from "./FloatingClinBreath";
import FloatingClinFun from "./FloatingClinFun";
import ClinPi from "./ClinPi";
import ClinGo from "./ClinGo";
import OfflineBanner from "./OfflineBanner";
import SyncStatus from "./SyncStatus";
import TrialBanner from "./TrialBanner";
import ProfileReminder from "./ProfileReminder";
import { isPremium, inTrial, startProCheckout } from "../lib/premium";
import { openTransfer } from "../lib/transfer";
import { api } from "../lib/api";

const nav = [
  { to: "/dashboard", label: "Inicio", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/pacientes", label: "Pacientes", icon: Users, testid: "nav-patients" },
  { to: "/agenda", label: "Agenda", icon: CalendarDays, testid: "nav-agenda" },
  { to: "/odontograma", label: "ClinDent", icon: ToothIcon, testid: "nav-odontogram" },
  { to: "/estadisticas", label: "ClinPed Analytics", icon: BarChart3, testid: "nav-stats" },
  { to: "/bitacora", label: "Bitácora", icon: ScrollText, testid: "nav-bitacora" },
  { to: "/perfil", label: "Mi Perfil", icon: UserCircle, testid: "nav-profile" },
];

export default function AppLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem("clinped_theme") === "dark");
  const premium = isPremium(user);
  const trial = inTrial(user);
  const teamAccess = premium || user?.has_team || user?.role === "admin";

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("clinped_theme", dark ? "dark" : "light");
  }, [dark]);

  // Notificador global: avisa cuando Enfermería marca un paciente como "listo"
  const seenQueue = useRef(null);
  useEffect(() => {
    if (!user) return;
    if ("Notification" in window && Notification.permission === "default") {
      try { Notification.requestPermission(); } catch { /* ignore */ }
    }
    const beep = () => {
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        const ctx = new AC();
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.type = "sine"; o.frequency.value = 880;
        g.gain.setValueAtTime(0.0001, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.5);
        o.start(); o.stop(ctx.currentTime + 0.5);
      } catch { /* ignore */ }
    };
    const notify = (name) => {
      beep();
      toast.success(`Paciente listo: ${name}`, {
        description: "Enfermería lo marcó como listo para pasar.",
        action: { label: "Ver", onClick: () => navigate("/dashboard") },
        duration: 8000,
      });
      if ("Notification" in window && Notification.permission === "granted") {
        try {
          const n = new Notification("Paciente listo", { body: `${name} está listo para pasar.`, tag: "clinped-ready" });
          n.onclick = () => { window.focus(); navigate("/dashboard"); };
        } catch { /* ignore */ }
      }
    };
    const poll = async () => {
      try {
        const r = await api.get("/queue");
        const ids = new Set((r.data || []).map((q) => q.patient_id));
        if (seenQueue.current === null) { seenQueue.current = ids; return; }
        (r.data || []).forEach((q) => { if (!seenQueue.current.has(q.patient_id)) notify(q.patient_name); });
        seenQueue.current = ids;
      } catch { /* ignore */ }
    };
    poll();
    const id = setInterval(poll, 15000);
    return () => clearInterval(id);
  }, [user, navigate]);

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="px-6 py-7">
        <Logo size={38} />
      </div>
      <nav className="flex-1 px-4 space-y-1.5">
        {[...nav.filter((i) => i.to !== "/odontograma" || user?.profile_type === "odontologico" || user?.has_odontogram), ...(user?.role === "admin" ? [{ to: "/admin", label: "Admin", icon: ShieldCheck, testid: "nav-admin" }] : [])].map((item) => {
          const active = location.pathname === item.to || location.pathname.startsWith(item.to + "/");
          return (
            <button
              key={item.to}
              data-testid={item.testid}
              onClick={() => { navigate(item.to); setOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl font-nunito font-semibold text-sm transition-all ${
                active
                  ? "bg-brand-terracotta text-white shadow-lg shadow-brand-terracotta/20"
                  : "text-brand-ink/70 hover:bg-brand-terracotta/10 hover:text-brand-terracotta"
              }`}
            >
              <item.icon size={20} />
              {item.label}
            </button>
          );
        })}
      </nav>
      {teamAccess && (
        <div className="px-4 pt-2 pb-1">
          <p className="px-4 text-[10px] font-bold uppercase tracking-wider text-brand-ink/40 mb-1.5">Accesos delegados</p>
          <button
            data-testid="nav-team"
            onClick={() => { navigate("/equipo"); setOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-2xl font-nunito font-semibold text-sm transition-all ${
              location.pathname === "/equipo"
                ? "bg-brand-sage text-white shadow-lg shadow-brand-sage/20"
                : "text-brand-ink/70 hover:bg-brand-sage/10 hover:text-brand-sageDark"
            }`}
          >
            <Users2 size={19} /> Equipo / Accesos
          </button>
        </div>
      )}
      <div className="p-4 border-t border-stone-100 space-y-2">
        <SyncStatus />
        {isPremium(user) ? (
          <div data-testid="plan-pro-badge" className="flex items-center gap-2 px-4 py-2 rounded-2xl bg-brand-terracotta/10 text-brand-terracotta font-nunito font-bold text-sm">
            <Gem size={16} /> Plan Pro {user?.role === "admin" ? "(admin)" : ""}
          </div>
        ) : (
          <button data-testid="upgrade-pro-btn" onClick={startProCheckout}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-2xl font-nunito font-semibold text-sm text-white bg-gradient-to-r from-brand-terracotta to-brand-terracottaDark hover:opacity-90 transition-all">
            <Gem size={18} /> Mejorar a Plan Pro
          </button>
        )}
        {!isPremium(user) && (
          <button data-testid="sidebar-transfer-link" onClick={() => openTransfer("pro")}
            className="w-full flex items-center justify-center gap-1.5 text-[11px] font-semibold text-brand-sageDark hover:underline">
            <Landmark size={12} /> ¿Falló tu pago? Paga por transferencia
          </button>
        )}
        <button data-testid="theme-toggle" onClick={() => setDark((d) => !d)}
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-2xl font-nunito font-semibold text-sm text-brand-ink/70 hover:bg-brand-terracotta/10 hover:text-brand-terracotta transition-all">
          {dark ? <Sun size={18} /> : <Moon size={18} />} {dark ? "Modo claro" : "Modo oscuro"}
        </button>
        <div className="flex items-center gap-3 px-2 py-2">
          <Avatar className="h-9 w-9 ring-2 ring-brand-sage/30">
            <AvatarImage src={user?.photo || user?.picture} />
            <AvatarFallback className="bg-brand-sage text-white text-xs">
              {user?.name?.[0] || "D"}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="font-outfit font-semibold text-sm text-brand-ink truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.specialty || "Pediatría"}</p>
          </div>
          <button data-testid="logout-btn" onClick={logout} className="text-muted-foreground hover:text-destructive transition-colors">
            <LogOut size={18} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background">
      <OfflineBanner />
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 bg-white border-r border-stone-100 flex-col fixed h-screen">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setOpen(false)} className="lg:hidden fixed inset-0 bg-black/30 z-40" />
            <motion.aside initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }}
              transition={{ type: "spring", damping: 24, stiffness: 220 }}
              className="lg:hidden fixed left-0 top-0 h-screen w-64 bg-white z-50 border-r border-stone-100">
              <SidebarContent />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="lg:hidden sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-stone-100 px-4 py-3 flex items-center justify-between">
          <Logo size={32} />
          <div className="flex items-center gap-2">
            <div className="max-w-[9rem]"><SyncStatus /></div>
            <button data-testid="mobile-menu-btn" onClick={() => setOpen(true)} className="p-2 text-brand-ink">
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </header>
        <TrialBanner />
        <ProfileReminder />
        <main className="p-5 sm:p-8 max-w-7xl mx-auto">{children}</main>
      </div>
      {(premium || trial || user?.has_clinpulse) && <FloatingClinPulse />}
      <FloatingClinIV />
      {(premium || trial || user?.has_clinbreath) && <FloatingClinBreath />}
      <FloatingCalculator />
      <FloatingClinFun />
      <ClinPi />
      <ClinGo />
    </div>
  );
}
